import React, { useState, useEffect, useCallback } from 'react';
import { Mail, Search, Eye, EyeOff, AlertCircle, CheckCircle, Clock, Send, ChevronLeft, ChevronRight, Filter, Trash2 } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { adminAPI } from '../lib/api';
import { toast } from 'sonner';

const TYPE_LABELS = {
    offre: { label: 'Offre', color: 'bg-purple-100 text-purple-700' },
    commande: { label: 'Commande', color: 'bg-blue-100 text-blue-700' },
    bienvenue: { label: 'Bienvenue', color: 'bg-green-100 text-green-700' },
    reset: { label: 'Mot de passe', color: 'bg-amber-100 text-amber-700' },
    autre: { label: 'Autre', color: 'bg-gray-100 text-gray-600' },
};

const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const d = new Date(dateStr);
    return d.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

export const AdminEmails = () => {
    const [logs, setLogs] = useState([]);
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [search, setSearch] = useState('');
    const [typeFilter, setTypeFilter] = useState('all');
    const [statusFilter, setStatusFilter] = useState('all');

    const loadLogs = useCallback(async () => {
        setLoading(true);
        try {
            const params = { page, limit: 30 };
            if (typeFilter !== 'all') params.email_type = typeFilter;
            if (statusFilter !== 'all') params.status = statusFilter;
            if (search) params.search = search;
            const res = await adminAPI.getEmailLogs(params);
            setLogs(res.data.logs || []);
            setTotalPages(res.data.pages || 1);
            setStats(res.data.stats || null);
        } catch (err) {
            console.error('Error loading email logs:', err);
        } finally {
            setLoading(false);
        }
    }, [page, typeFilter, statusFilter, search]);

    useEffect(() => { loadLogs(); }, [loadLogs]);

    const handleSearch = (e) => {
        e.preventDefault();
        setPage(1);
        loadLogs();
    };

    const handleClearHistory = async () => {
        if (!window.confirm('Êtes-vous sûr de vouloir effacer tout l\'historique des emails ?\nCette action est irréversible. Sauvegardez la BDD avant.')) return;
        try {
            await adminAPI.clearEmailHistory();
            toast.success('Historique des emails effacé');
            loadLogs();
        } catch (err) {
            toast.error('Erreur lors de la suppression');
        }
    };

    return (
        <div className="space-y-6" data-testid="admin-emails-page">
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-leaf/10 flex items-center justify-center">
                        <Mail className="w-5 h-5 text-leaf" />
                    </div>
                    <div>
                        <h1 className="font-heading text-2xl font-bold text-jungle">Suivi des Emails</h1>
                        <p className="text-sm text-muted-foreground">Historique et suivi d'ouverture de tous les emails</p>
                    </div>
                </div>
                <Button
                    variant="outline"
                    className="border-red-300 text-red-600 hover:bg-red-50 rounded-xl"
                    onClick={handleClearHistory}
                    data-testid="clear-email-history-btn"
                >
                    <Trash2 className="w-4 h-4 mr-2" />
                    Effacer historique
                </Button>
            </div>

            {stats && (
                <div className="grid grid-cols-2 sm:grid-cols-5 gap-3" data-testid="email-stats-summary">
                    <Card className="border-jungle/10">
                        <CardContent className="p-4 text-center">
                            <Send className="w-5 h-5 mx-auto mb-1 text-jungle" />
                            <div className="text-2xl font-bold text-jungle">{stats.total}</div>
                            <div className="text-xs text-muted-foreground">Total</div>
                        </CardContent>
                    </Card>
                    <Card className="border-jungle/10">
                        <CardContent className="p-4 text-center">
                            <CheckCircle className="w-5 h-5 mx-auto mb-1 text-green-600" />
                            <div className="text-2xl font-bold text-green-600">{stats.sent}</div>
                            <div className="text-xs text-muted-foreground">Envoyés</div>
                        </CardContent>
                    </Card>
                    <Card className="border-jungle/10">
                        <CardContent className="p-4 text-center">
                            <Eye className="w-5 h-5 mx-auto mb-1 text-blue-600" />
                            <div className="text-2xl font-bold text-blue-600">{stats.opened}</div>
                            <div className="text-xs text-muted-foreground">Ouverts</div>
                        </CardContent>
                    </Card>
                    <Card className="border-jungle/10">
                        <CardContent className="p-4 text-center">
                            <AlertCircle className="w-5 h-5 mx-auto mb-1 text-red-500" />
                            <div className="text-2xl font-bold text-red-500">{stats.errors}</div>
                            <div className="text-xs text-muted-foreground">Erreurs</div>
                        </CardContent>
                    </Card>
                    <Card className="border-jungle/10">
                        <CardContent className="p-4 text-center">
                            <div className="w-5 h-5 mx-auto mb-1 rounded-full bg-leaf/20 flex items-center justify-center text-xs font-bold text-leaf">%</div>
                            <div className="text-2xl font-bold text-leaf">{stats.open_rate}%</div>
                            <div className="text-xs text-muted-foreground">Taux ouverture</div>
                        </CardContent>
                    </Card>
                </div>
            )}

            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4 text-sm text-amber-700">
                <strong>Note :</strong> Le suivi d'ouverture utilise un pixel invisible dans l'email. Certains clients email (Gmail, Outlook) pré-chargent automatiquement les images via leurs serveurs proxy, ce qui peut fausser les statistiques. Le statut "Ouvert" n'est donc pas fiable à 100%.
            </div>

            <Card className="border-jungle/10">
                <CardHeader className="pb-3">
                    <div className="flex flex-col sm:flex-row gap-3">
                        <form onSubmit={handleSearch} className="flex-1 flex gap-2">
                            <div className="relative flex-1">
                                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                                <Input
                                    placeholder="Rechercher par email ou sujet..."
                                    value={search}
                                    onChange={(e) => setSearch(e.target.value)}
                                    className="pl-10 bg-white/80 border-jungle/20 rounded-xl"
                                    data-testid="email-search-input"
                                />
                            </div>
                            <Button type="submit" variant="secondary" size="sm" className="rounded-xl">
                                <Search className="w-4 h-4" />
                            </Button>
                        </form>
                        <div className="flex gap-2">
                            <Select value={typeFilter} onValueChange={(v) => { setTypeFilter(v); setPage(1); }}>
                                <SelectTrigger className="w-[150px] bg-white/80 border-jungle/20 rounded-xl" data-testid="email-type-filter">
                                    <Filter className="w-3 h-3 mr-1" />
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="all">Tous les types</SelectItem>
                                    <SelectItem value="offre">Offres</SelectItem>
                                    <SelectItem value="commande">Commandes</SelectItem>
                                    <SelectItem value="bienvenue">Bienvenue</SelectItem>
                                    <SelectItem value="reset">Mot de passe</SelectItem>
                                    <SelectItem value="autre">Autre</SelectItem>
                                </SelectContent>
                            </Select>
                            <Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v); setPage(1); }}>
                                <SelectTrigger className="w-[150px] bg-white/80 border-jungle/20 rounded-xl" data-testid="email-status-filter">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="all">Tous statuts</SelectItem>
                                    <SelectItem value="sent">Envoyé (non ouvert)</SelectItem>
                                    <SelectItem value="opened">Ouvert</SelectItem>
                                    <SelectItem value="error">Erreur</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                    </div>
                </CardHeader>
                <CardContent>
                    {loading ? (
                        <div className="space-y-3">
                            {[1,2,3,4,5].map(i => (
                                <div key={i} className="h-14 bg-jungle/5 animate-pulse rounded-xl" />
                            ))}
                        </div>
                    ) : logs.length === 0 ? (
                        <div className="text-center py-12 text-muted-foreground">
                            <Mail className="w-10 h-10 mx-auto mb-3 opacity-30" />
                            <p>Aucun email trouvé</p>
                        </div>
                    ) : (
                        <div className="space-y-2">
                            {logs.map((email) => {
                                const typeInfo = TYPE_LABELS[email.type] || TYPE_LABELS.autre;
                                return (
                                    <div
                                        key={email.id}
                                        className="flex items-center gap-3 p-3 rounded-xl bg-gray-50/80 hover:bg-gray-100/80 transition-colors"
                                        data-testid={`email-log-${email.id}`}
                                    >
                                        <div className="flex-shrink-0">
                                            {email.status === 'error' ? (
                                                <div className="w-9 h-9 rounded-full bg-red-100 flex items-center justify-center">
                                                    <AlertCircle className="w-4 h-4 text-red-500" />
                                                </div>
                                            ) : email.opened ? (
                                                <div className="w-9 h-9 rounded-full bg-blue-100 flex items-center justify-center">
                                                    <Eye className="w-4 h-4 text-blue-600" />
                                                </div>
                                            ) : (
                                                <div className="w-9 h-9 rounded-full bg-gray-100 flex items-center justify-center">
                                                    <EyeOff className="w-4 h-4 text-gray-400" />
                                                </div>
                                            )}
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-2 mb-0.5">
                                                <span className="font-medium text-sm text-jungle truncate">
                                                    {email.to?.join(', ')}
                                                </span>
                                                <Badge className={`text-[10px] px-1.5 py-0 ${typeInfo.color}`}>
                                                    {typeInfo.label}
                                                </Badge>
                                            </div>
                                            <p className="text-xs text-muted-foreground truncate">{email.subject}</p>
                                        </div>
                                        <div className="flex-shrink-0 text-right">
                                            <div className="text-xs text-muted-foreground">{formatDate(email.sent_at)}</div>
                                            {email.status === 'error' ? (
                                                <span className="text-[10px] text-red-500 font-medium">Erreur</span>
                                            ) : email.opened ? (
                                                <span className="text-[10px] text-blue-600 font-medium">
                                                    Ouvert {email.opened_at ? formatDate(email.opened_at) : ''}
                                                </span>
                                            ) : (
                                                <span className="text-[10px] text-gray-400 flex items-center justify-end gap-1">
                                                    <Clock className="w-3 h-3" /> Non ouvert
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}

                    {totalPages > 1 && (
                        <div className="flex items-center justify-center gap-2 mt-4 pt-4 border-t">
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={() => setPage(p => Math.max(1, p - 1))}
                                disabled={page <= 1}
                                className="rounded-xl"
                                data-testid="email-prev-page"
                            >
                                <ChevronLeft className="w-4 h-4" />
                            </Button>
                            <span className="text-sm text-muted-foreground">
                                Page {page} / {totalPages}
                            </span>
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                                disabled={page >= totalPages}
                                className="rounded-xl"
                                data-testid="email-next-page"
                            >
                                <ChevronRight className="w-4 h-4" />
                            </Button>
                        </div>
                    )}
                </CardContent>
            </Card>
        </div>
    );
};

export default AdminEmails;
